import React, { Fragment, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PlusOutlined } from '@ant-design/icons'

import { ExtendedTable, SubheaderToolbarDivider } from '@app/components'
import { LntButton } from '@app/components/base'
import { TableSearchButton } from '@app/components/TableSettingsToolbar'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { TRANSLATIONS_CONFIG_KEY } from '@app/constants/queryKeys'

import { useNotification, usePaginationParams, useRemoveTableRowButton } from '@app/hooks'
import { getTranslationsConfigClustersDataFromResponse } from '@app/utils/responseAdapters/getTranslationsConfigDataFromResponse'

import { deleteTranslationsClusters, getTranslationsConfigClusters } from '@app/api/routes/translationsConfig'

import { getColumns } from './utils'

import type { ClusterTabProps } from './types'
import type { TranslationsConfigClustersDataSource } from '@app/types/dataSource/TranslationsConfigDataSource'

const columns = getColumns()

export const ClusterTab: React.FC<ClusterTabProps> = props => {
  const { onOpenAddClustersDrawer } = props

  const queryClient = useQueryClient()
  const notification = useNotification()

  const { pagination, setPagination, paginationParams } = usePaginationParams()

  const { data: translationsClustersData, isLoading: isTranslationsClustersDataLoading } = useQuery({
    queryKey: [TRANSLATIONS_CONFIG_KEY.clusters],
    queryFn: async () => await getTranslationsConfigClusters({ ...paginationParams })
  })

  const deleteTranslationsClusterMutation = useMutation({
    mutationFn: deleteTranslationsClusters,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [TRANSLATIONS_CONFIG_KEY.clusters] })

      notification.success({
        message: 'Выбранный кластер удален из транслирования.'
      })
    }
  })

  const dataSource = useMemo(
    () => getTranslationsConfigClustersDataFromResponse(translationsClustersData),
    [translationsClustersData]
  )

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleDeleteCluster = (rowKey: string): void => {
    deleteTranslationsClusterMutation.mutate({ clusterSapId: rowKey })
  }

  useRemoveTableRowButton({
    columns,
    onClick: rowKey => handleDeleteCluster(String(rowKey))
  })

  const toolbar = [
    <Fragment key='add-clusters'>
      <LntButton
        icon={ <PlusOutlined /> }
        type={ BUTTON_TYPE.primary }
        size={ SIZE.small }
        onClick={ onOpenAddClustersDrawer }
      >
        Кластер
      </LntButton>

      <SubheaderToolbarDivider />
    </Fragment>,

    // TODO: Добавить табличный фильтр
    <TableSearchButton key='search-clusters' />
  ]

  return (
    <ExtendedTable<TranslationsConfigClustersDataSource>
      loading={ isTranslationsClustersDataLoading }
      toolbar={ toolbar }
      columns={ columns }
      dataSource={ dataSource }
      pagination={ {
        ...pagination,
        total: translationsClustersData?.count,
        onChange: handlePaginationChange
      } }
    />
  )
}
